import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Global cache for preloaded scenario
let cachedScenario: {
  background_type: string;
  background_video_url: string | null;
  landscape_video_url?: string | null;
  background_image_url: string | null;
  title: string | null;
  subtitle: string | null;
  show_title: boolean;
  show_subtitle: boolean;
  show_loading_animation: boolean;
  text_color: string | null;
  overlay_opacity: number | null;
  is_active: boolean;
  is_original: boolean;
} | null = null;

let preloadPromise: Promise<void> | null = null;
let videoPreloaded = false;
let preloadedVideoUrl: string | null = null;
let preloadedVideoEl: HTMLVideoElement | null = null;
let preloadStarted = false;
let landscapeVideoPreloaded = false;
let preloadedLandscapeVideoEl: HTMLVideoElement | null = null;

/**
 * Get the cached scenario if available
 */
export const getCachedScenario = () => cachedScenario;

export const getPreloadedVideoUrl = () => preloadedVideoUrl;

/**
 * Check if video has been preloaded
 */
export const isVideoPreloaded = () => {
  // Check both portrait and landscape videos
  const portraitReady = (preloadedVideoEl && preloadedVideoEl.readyState >= 2) || videoPreloaded;
  const landscapeReady = (preloadedLandscapeVideoEl && preloadedLandscapeVideoEl.readyState >= 2) || landscapeVideoPreloaded;
  return portraitReady || landscapeReady;
};

export const isLandscapeVideoPreloaded = () => {
  if (preloadedLandscapeVideoEl && preloadedLandscapeVideoEl.readyState >= 2) return true;
  return landscapeVideoPreloaded;
};

/**
 * Detect if running on iOS
 */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/i.test(ua) || 
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

/**
 * Detect Safari desktop (not iOS)
 */
const isSafariDesktop = () => {
  if (typeof navigator === 'undefined') return false;
  return !isIOS() && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

/**
 * Detect if device should use landscape video (desktop/tablet/wide screens)
 */
const shouldUseLandscapeVideo = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768 || window.innerWidth > window.innerHeight;
};

/**
 * Trigger preloading of loading screen video/image immediately
 * Call this when user starts the quiz to ensure video is ready by loading screen
 */
export const triggerLoadingScreenPreload = async () => {
  if (preloadStarted) return; // Already started
  preloadStarted = true;
  
  try {
    // Fetch the active scenario
    const { data: activeData } = await supabase
      .from("loading_screen_scenarios")
      .select("*")
      .eq("is_active", true)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const scenario = activeData || await getOriginalScenario();
    
    if (!scenario) return;

    // Cache the scenario for instant access
    cachedScenario = scenario;

    // Preload ONLY the video needed for current device to avoid bandwidth competition.
    // IMPORTANT: On iOS, prefer .mp4 over .mov because Safari has trouble with .mov in <video>.
    // This must match the same logic used in LoadingScreen's preferredVideoUrl.
    if (scenario.background_type === "video") {
      const portrait = scenario.background_video_url;
      const landscape = scenario.landscape_video_url;
      const isiOSDevice = isIOS();
      const useLandscape = shouldUseLandscapeVideo();

      // iOS portrait: always use portrait video (even .mov) to avoid zoomed-in landscape.
      // Only swap to landscape .mp4 when the device is actually in landscape orientation.
      if (
        isiOSDevice &&
        useLandscape &&
        portrait &&
        /\.mov($|\?)/i.test(portrait) &&
        landscape &&
        /\.mp4($|\?)/i.test(landscape)
      ) {
        await preloadVideo(landscape, true);
      } else if (useLandscape && landscape) {
        await preloadVideo(landscape, true);
      } else if (portrait) {
        await preloadVideo(portrait, false);
      }
    }

    // Preload image
    if (scenario.background_type === "image" && scenario.background_image_url) {
      preloadImage(scenario.background_image_url);
    }
  } catch {
    // Silently handle preload errors - loading screen will still work
  }
};

const getOriginalScenario = async () => {
  try {
    const { data } = await supabase
      .from("loading_screen_scenarios")
      .select("*")
      .eq("is_original", true)
      .limit(1)
      .maybeSingle();
    return data;
  } catch {
    return null;
  }
};

/**
 * Preloads the active loading screen video/image on app initialization
 * This ensures faster display when the loading screen is shown after the quiz
 */
export const usePreloadLoadingScreen = () => {
  useEffect(() => {
    const preloadActiveScenario = async () => {
      // Fetch the active scenario
      const { data: activeData } = await supabase
        .from("loading_screen_scenarios")
        .select("*")
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .single();

      const scenario = activeData || await getOriginalScenario();
      
      if (!scenario) return;

      // Cache the scenario for instant access
      cachedScenario = scenario;

      // Preload ONLY the video needed for current device to avoid bandwidth competition.
      // IMPORTANT: On iOS, prefer .mp4 over .mov (same logic as LoadingScreen's preferredVideoUrl).
      if (scenario.background_type === "video") {
        const useLandscape = shouldUseLandscapeVideo();
        const portrait = scenario.background_video_url;
        const landscape = scenario.landscape_video_url;
        const isiOSDevice = isIOS();

        if (
          isiOSDevice &&
          useLandscape &&
          portrait &&
          /\.mov($|\?)/i.test(portrait) &&
          landscape &&
          /\.mp4($|\?)/i.test(landscape)
        ) {
          await preloadVideo(landscape, true);
        } else if (useLandscape && landscape) {
          await preloadVideo(landscape, true);
        } else if (portrait) {
          await preloadVideo(portrait, false);
        }
      }

      // Preload image
      if (scenario.background_type === "image" && scenario.background_image_url) {
        preloadImage(scenario.background_image_url);
      }
    };

    // Start preloading immediately - no delay for faster loading screen display
    if (!preloadPromise) {
      preloadPromise = preloadActiveScenario();
    }
  }, []);
};

const preloadVideo = (url: string, isLandscape = false): Promise<void> => {
  return new Promise((resolve) => {
    const isiOSDevice = isIOS();
    const isSafari = isSafariDesktop();
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    
    // Use link preload for Chrome/Edge/Firefox (desktop only, high priority)
    // Skip for Safari – it can cause CORS issues with Supabase storage
    if (!isiOSDevice && !isSafari && isDesktop) {
      const existingLink = document.querySelector(`link[href="${url}"]`);
      if (!existingLink) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "video";
        link.href = url;
        link.crossOrigin = "anonymous";
        (link as any).fetchpriority = "high";
        document.head.appendChild(link);
      }
    }

    // Select the correct video element based on orientation
    const videoElRef = isLandscape ? 'landscape' : 'portrait';
    let videoEl = isLandscape ? preloadedLandscapeVideoEl : preloadedVideoEl;

    // Create a single hidden video element
    if (!videoEl) {
      videoEl = document.createElement("video");
      videoEl.id = `loading-screen-preload-video-${videoElRef}`;
      // Desktop (non-Safari/iOS): use 'auto' to fully buffer
      // Safari/iOS: use 'metadata' to avoid blocking issues
      videoEl.preload = "auto"; // Always use 'auto' to actually buffer video data
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute("playsinline", "");
      videoEl.setAttribute("webkit-playsinline", "");
      videoEl.style.position = "fixed";
      videoEl.style.width = "1px";
      videoEl.style.height = "1px";
      videoEl.style.opacity = "0";
      videoEl.style.pointerEvents = "none";
      videoEl.style.zIndex = "-1";
      document.body.appendChild(videoEl);
      
      if (isLandscape) {
        preloadedLandscapeVideoEl = videoEl;
      } else {
        preloadedVideoEl = videoEl;
      }
    } else {
      // Update preload strategy if element already exists
      videoEl.preload = "auto";
    }

    if (!isLandscape) {
      preloadedVideoUrl = url;
    }

    // Avoid forcing CORS mode on iOS WebKit AND Safari desktop.
    if (!isiOSDevice && !isSafari) {
      videoEl.crossOrigin = "anonymous";
    } else {
      videoEl.removeAttribute("crossorigin");
    }

    videoEl.src = url;

    let resolved = false;
    const markReady = (forceResolve = false) => {
      if (resolved) return;
      const isActuallyReady = videoEl.readyState >= 2; // HAVE_CURRENT_DATA

      if (isActuallyReady) {
        if (isLandscape) {
          landscapeVideoPreloaded = true;
        } else {
          videoPreloaded = true;
        }
      }

      if (forceResolve || isActuallyReady) {
        resolved = true;
        resolve();
      }
    };

    const handleReadyEvent = () => markReady(false);

    videoEl.oncanplay = handleReadyEvent;
    videoEl.onloadeddata = handleReadyEvent;
    videoEl.oncanplaythrough = handleReadyEvent;

    // Desktop (non-iOS, non-Safari): aggressive buffering - play briefly to force load
    // Safari desktop can block this and waste a user-gesture opportunity
    if (isDesktop && !isiOSDevice && !isSafari) {
      videoEl.play().then(() => {
        // Pause after a brief moment - video is now buffered
        setTimeout(() => {
          if (videoEl && !videoEl.paused) {
            videoEl.pause();
            videoEl.currentTime = 0;
          }
          markReady(false);
        }, 50);
      }).catch(() => {
        // Autoplay blocked, just load
        videoEl.load();
      });
    } else {
      videoEl.load();
    }

    // Shorter timeout for faster resolution - video will continue loading in background
    setTimeout(() => markReady(true), isiOSDevice ? 3000 : isSafari ? 2500 : 2000);
  });
};

const preloadImage = (url: string) => {
  // Add preload link to head
  const existingLink = document.querySelector(`link[href="${url}"]`);
  if (!existingLink) {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    document.head.appendChild(link);
  }

  // Preload image
  const img = new Image();
  img.src = url;
};

export default usePreloadLoadingScreen;
